/**
 * Zoho Response Control Component
 *
 * Manages lead status-based response control for AI Agent system.
 * Provides UI for:
 * - Syncing lead statuses from Zoho CRM
 * - Managing entity settings (enable/disable toggle)
 * - Viewing and editing response mode mappings
 */

import React, { useState, useEffect } from 'react';
import { orchestratorApi } from '../api/orchestrator';

// ============================================================================
// Types
// ============================================================================

interface ResponseModeMapping {
  tenant_id: string;
  lead_status: string;
  response_mode: 'normal' | 'light' | 'none';
  enabled: boolean;
  priority: number;
  description?: string;
}

interface EntitySettings {
  tenant_id: string;
  entity_id: string;
  response_control_enabled: boolean;
  cache_ttl_seconds: number;
  fallback_to_normal: boolean;
}

interface Entity {
  entity_id: string;
  name: string;
  phone_number: string;
  [key: string]: any;
}

interface SyncResult {
  synced: number;
  failed: number;
  errors: string[];
}

// ============================================================================
// Component
// ============================================================================

export const ZohoResponseControl: React.FC = () => {
  // State
  const [entities, setEntities] = useState<Entity[]>([]);
  const [entitySettings, setEntitySettings] = useState<Map<string, EntitySettings>>(new Map());
  const [mappings, setMappings] = useState<ResponseModeMapping[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Editing state
  const [editingMapping, setEditingMapping] = useState<ResponseModeMapping | null>(null);
  const [editedResponseMode, setEditedResponseMode] = useState<'normal' | 'light' | 'none'>('normal');
  const [editedEnabled, setEditedEnabled] = useState(true);

  // Load data on mount
  useEffect(() => {
    loadEntities();
    loadMappings();
  }, []);

  // Load entities
  const loadEntities = async () => {
    try {
      const response = await fetch('/api/entities', {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        setEntities(data.entities || []);

        // Load settings for each entity
        for (const entity of data.entities || []) {
          loadEntitySettings(entity.entity_id);
        }
      }
    } catch (error) {
      console.error('Failed to load entities:', error);
      showMessage('error', 'Failed to load entities');
    } finally {
      setLoading(false);
    }
  };

  // Load entity settings
  const loadEntitySettings = async (entityId: string) => {
    try {
      const response = await fetch(`/api/admin/zoho/response-control/entity-settings?entity_id=${entityId}`, {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok && data.settings) {
        setEntitySettings(prev => new Map(prev).set(entityId, data.settings));
      }
    } catch (error) {
      console.error(`Failed to load settings for entity ${entityId}:`, error);
    }
  };

  // Load response mode mappings
  const loadMappings = async () => {
    try {
      const response = await fetch('/api/admin/zoho/response-control/mappings', {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        setMappings(data.mappings || []);
      }
    } catch (error) {
      console.error('Failed to load response mode mappings:', error);
      showMessage('error', 'Failed to load response mode mappings');
    }
  };

  // Sync lead statuses from Zoho
  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);

    try {
      const response = await fetch('/api/admin/zoho/response-control/sync', {
        method: 'POST',
        headers: await orchestratorApi.getAuthHeaders(),
        body: JSON.stringify({ limit: 100 })
      });

      const data = await response.json();

      if (data.ok) {
        setSyncResult(data.result);
        showMessage('success', `Sync completed: ${data.result.synced} leads synced, ${data.result.failed} failed`);
      } else {
        showMessage('error', data.error || 'Sync failed');
      }
    } catch (error) {
      console.error('Failed to sync lead statuses:', error);
      showMessage('error', 'Failed to sync lead statuses from CRM');
    } finally {
      setSyncing(false);
    }
  };

  // Toggle response control for entity
  const handleToggleEntity = async (entityId: string, enabled: boolean) => {
    try {
      const currentSettings = entitySettings.get(entityId);

      const response = await fetch('/api/admin/zoho/response-control/entity-settings', {
        method: 'POST',
        headers: await orchestratorApi.getAuthHeaders(),
        body: JSON.stringify({
          entity_id: entityId,
          response_control_enabled: enabled,
          cache_ttl_seconds: currentSettings?.cache_ttl_seconds || 3600,
          fallback_to_normal: currentSettings?.fallback_to_normal !== false
        })
      });

      const data = await response.json();

      if (data.ok) {
        await loadEntitySettings(entityId);
        showMessage('success', `Response control ${enabled ? 'enabled' : 'disabled'} for entity`);
      } else {
        showMessage('error', data.error || 'Failed to update entity settings');
      }
    } catch (error) {
      console.error('Failed to toggle entity response control:', error);
      showMessage('error', 'Failed to update entity settings');
    }
  };

  // Start editing mapping
  const startEditing = (mapping: ResponseModeMapping) => {
    setEditingMapping(mapping);
    setEditedResponseMode(mapping.response_mode);
    setEditedEnabled(mapping.enabled);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingMapping(null);
    setEditedResponseMode('normal');
    setEditedEnabled(true);
  };

  // Save mapping
  const saveMapping = async () => {
    if (!editingMapping) return;

    try {
      const response = await fetch('/api/admin/zoho/response-control/mappings', {
        method: 'POST',
        headers: await orchestratorApi.getAuthHeaders(),
        body: JSON.stringify({
          lead_status: editingMapping.lead_status,
          response_mode: editedResponseMode,
          enabled: editedEnabled
        })
      });

      const data = await response.json();

      if (data.ok) {
        await loadMappings();
        cancelEditing();
        showMessage('success', 'Response mode mapping updated');
      } else {
        showMessage('error', data.error || 'Failed to update mapping');
      }
    } catch (error) {
      console.error('Failed to update mapping:', error);
      showMessage('error', 'Failed to update response mode mapping');
    }
  };

  // Show message
  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // Get response mode badge color
  const getResponseModeBadgeColor = (mode: 'normal' | 'light' | 'none') => {
    switch (mode) {
      case 'normal':
        return '#10b981'; // green
      case 'light':
        return '#f59e0b'; // amber
      case 'none':
        return '#ef4444'; // red
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div className="spinner"></div>
        <p>Loading CRM Response Control...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '20px' }}>CRM Response Control</h2>

      {message && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: message.type === 'success' ? '#d4edda' : message.type === 'error' ? '#f8d7da' : '#d1ecf1',
          color: message.type === 'success' ? '#155724' : message.type === 'error' ? '#721c24' : '#0c5460',
          border: `1px solid ${message.type === 'success' ? '#c3e6cb' : message.type === 'error' ? '#f5c6cb' : '#bee5eb'}`
        }}>
          {message.text}
        </div>
      )}

      {/* Legend */}
      <div style={{
        padding: '15px',
        marginBottom: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        border: '1px solid #dee2e6'
      }}>
        <h4 style={{ marginTop: 0, marginBottom: '10px' }}>Response Modes</h4>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: getResponseModeBadgeColor('normal')
            }}></div>
            <strong>Normal:</strong> Full AI Agent response
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: getResponseModeBadgeColor('light')
            }}></div>
            <strong>Light:</strong> Fixed acknowledgment message
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: getResponseModeBadgeColor('none')
            }}></div>
            <strong>None:</strong> No response at all
          </div>
        </div>
      </div>

      {/* Sync Section */}
      <div style={{
        marginBottom: '30px',
        padding: '15px',
        backgroundColor: '#e7f3ff',
        borderRadius: '4px',
        border: '1px solid #b3d9ff'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Sync Lead Statuses from CRM</h3>
        <p style={{ marginBottom: '15px', color: '#495057' }}>
          Fetch the latest lead statuses from your CRM and update the local cache.
          This ensures response control decisions use up-to-date lead information.
        </p>
        <button
          onClick={handleSync}
          disabled={syncing}
          style={{
            padding: '10px 20px',
            backgroundColor: syncing ? '#6c757d' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: syncing ? 'not-allowed' : 'pointer',
            fontSize: '14px'
          }}
        >
          {syncing ? 'Syncing...' : 'Sync from CRM'}
        </button>

        {syncResult && (
          <div style={{
            marginTop: '15px',
            padding: '10px',
            backgroundColor: syncResult.synced > 0 ? '#d4edda' : '#f8d7da',
            borderRadius: '4px',
            border: `1px solid ${syncResult.synced > 0 ? '#c3e6cb' : '#f5c6cb'}`
          }}>
            <strong>Sync Result:</strong>
            <ul style={{ margin: '5px 0 0 20px', padding: 0 }}>
              <li>Synced: {syncResult.synced}</li>
              <li>Failed: {syncResult.failed}</li>
              {syncResult.errors.length > 0 && (
                <li>Errors: {syncResult.errors.join(', ')}</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Entity Settings */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '15px' }}>Entity Settings</h3>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Entity ID</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Entity Name</th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Response Control</th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Cache TTL</th>
            </tr>
          </thead>
          <tbody>
            {entities.map(entity => {
              const settings = entitySettings.get(entity.entity_id);
              return (
                <tr key={entity.entity_id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px' }}>{entity.entity_id}</td>
                  <td style={{ padding: '12px' }}>{entity.name || 'N/A'}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={settings?.response_control_enabled || false}
                        onChange={(e) => handleToggleEntity(entity.entity_id, e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '12px', color: '#6c757d' }}>
                        {settings?.response_control_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {settings?.cache_ttl_seconds ? `${Math.floor(settings.cache_ttl_seconds / 60)} min` : 'N/A'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Response Mode Mappings */}
      <div>
        <h3 style={{ marginBottom: '15px' }}>Response Mode Mappings</h3>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Lead Status</th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Response Mode</th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Enabled</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Description</th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {mappings.map(mapping => {
              const isEditing = editingMapping?.lead_status === mapping.lead_status && editingMapping?.tenant_id === mapping.tenant_id;

              return (
                <tr key={`${mapping.tenant_id}-${mapping.lead_status}`} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px' }}>{mapping.lead_status}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {isEditing ? (
                      <select
                        value={editedResponseMode}
                        onChange={(e) => setEditedResponseMode(e.target.value as 'normal' | 'light' | 'none')}
                        style={{
                          padding: '6px',
                          borderRadius: '4px',
                          border: '1px solid #ced4da',
                          fontSize: '14px'
                        }}
                      >
                        <option value="normal">Normal</option>
                        <option value="light">Light</option>
                        <option value="none">None</option>
                      </select>
                    ) : (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: getResponseModeBadgeColor(mapping.response_mode),
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {mapping.response_mode.toUpperCase()}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {isEditing ? (
                      <input
                        type="checkbox"
                        checked={editedEnabled}
                        onChange={(e) => setEditedEnabled(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                    ) : (
                      <span style={{ fontSize: '12px', color: mapping.enabled ? '#28a745' : '#6c757d' }}>
                        {mapping.enabled ? '✓ Yes' : '✗ No'}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px', fontSize: '13px', color: '#6c757d' }}>
                    {mapping.description || '-'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={saveMapping}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEditing}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEditing(mapping)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
